/**
 * Network Status Details Dialog
 * Shows detailed network status information for a specific exchange
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Grid,
} from '@mui/material';
import type { NetworkStatusResponse } from '../../types/networkStatus';
import { EXCHANGES } from '../../types/opportunity';

interface NetworkStatusDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  exchange: number;
  networkStatus: NetworkStatusResponse | null;
}

export const NetworkStatusDetailsDialog: React.FC<NetworkStatusDetailsDialogProps> = ({
  open,
  onClose,
  exchange,
  networkStatus,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  
  const exchangeName = EXCHANGES[exchange] || 'Unknown';
  
  // Filter cryptos based on search query
  const filteredCryptos = React.useMemo(() => {
    if (!networkStatus?.cryptos || !Array.isArray(networkStatus.cryptos)) {
      return [];
    }
    
    if (!searchQuery) {
      return networkStatus.cryptos;
    }
    
    const query = searchQuery.toLowerCase();
    return networkStatus.cryptos.filter(crypto =>
      crypto.symbol.toLowerCase().includes(query) ||
      crypto.name.toLowerCase().includes(query)
    );
  }, [networkStatus, searchQuery]);
  
  // Calculate statistics
  const stats = React.useMemo(() => {
    if (!networkStatus?.cryptos || !Array.isArray(networkStatus.cryptos)) {
      return {
        total: 0,
        depositEnabled: 0,
        withdrawEnabled: 0,
        bothEnabled: 0,
        uniqueNetworks: new Set<string>(),
      };
    }
    
    let depositEnabled = 0;
    let withdrawEnabled = 0;
    let bothEnabled = 0;
    const uniqueNetworks = new Set<string>();
    
    networkStatus.cryptos.forEach(crypto => {
      let hasDeposit = false;
      let hasWithdraw = false;
      
      crypto.networks?.forEach(network => {
        uniqueNetworks.add(network.network);
        if (network.deposit_enabled) hasDeposit = true;
        if (network.withdraw_enabled) hasWithdraw = true;
      });
      
      if (hasDeposit) depositEnabled++;
      if (hasWithdraw) withdrawEnabled++;
      if (hasDeposit && hasWithdraw) bothEnabled++;
    });
    
    return {
      total: networkStatus.cryptos.length,
      depositEnabled,
      withdrawEnabled,
      bothEnabled,
      uniqueNetworks,
    };
  }, [networkStatus]);
  
  const formatFee = (fee: number | undefined) => {
    if (fee === undefined) return '-';
    return fee.toFixed(8).replace(/\.?0+$/, '');
  };
  
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {exchangeName} Network Status
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Last Updated: {networkStatus ? new Date(networkStatus.timestamp).toLocaleString() : 'N/A'}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {networkStatus ? (
          <>
            {/* Statistics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h4">{stats.total}</Typography>
                  <Typography variant="caption">Total Cryptos</Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h4" color="success.main">{stats.depositEnabled}</Typography>
                  <Typography variant="caption">Deposit Enabled</Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h4" color="warning.main">{stats.withdrawEnabled}</Typography>
                  <Typography variant="caption">Withdraw Enabled</Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="h4">{stats.uniqueNetworks.size}</Typography>
                  <Typography variant="caption">Networks</Typography>
                </Box>
              </Grid>
            </Grid>
            
            {/* Search */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by symbol or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">Search</InputAdornment>
                ),
              }}
            />
            
            {/* Cryptos Table */}
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Network</TableCell>
                    <TableCell align="center">Deposit</TableCell>
                    <TableCell align="center">Withdraw</TableCell>
                    <TableCell align="center">Confirmations</TableCell>
                    <TableCell align="right">Withdraw Fee</TableCell>
                    <TableCell align="right">Min Withdraw</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCryptos.map((crypto) => (
                    crypto.networks?.map((network, index) => (
                      <TableRow key={`${crypto.symbol}-${network.network}`} hover>
                        <TableCell>
                          {index === 0 ? (
                            <Typography variant="body2" fontWeight="bold">
                              {crypto.symbol}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {index === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              {crypto.name}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            [{network.network}]{network.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {network.deposit_enabled ? '✅' : '❌'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {network.withdraw_enabled ? '✅' : '❌'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {network.confirmations ?? '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatFee(network.withdraw_fee)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {network.min_withdraw ? formatFee(network.min_withdraw) : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Legend */}
            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip size="small" sx={{ bgcolor: '#4caf50', width: 20, height: 20 }} />
                <Typography variant="caption">Deposit & Withdraw</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip size="small" sx={{ bgcolor: '#ff9800', width: 20, height: 20 }} />
                <Typography variant="caption">Partial</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip size="small" sx={{ bgcolor: '#9e9e9e', width: 20, height: 20 }} />
                <Typography variant="caption">Disabled</Typography>
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No network status data available for {exchangeName}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};